import React, { useEffect, useState, useRef } from 'react';
import { fetchCategories } from './api';
import { Box, Button, useTheme, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useCart } from './CartContext';
import { isBranchOpen } from './branchUtils';

function Categories() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const scrollRef = useRef(null);
  const { selectedBranch } = useCart();
  const [branchStatus, setBranchStatus] = useState({ isOpen: true });

  useEffect(() => {
    if (selectedBranch) {
      setBranchStatus(isBranchOpen(selectedBranch));
    }
  }, [selectedBranch]);

  useEffect(() => {
    fetchCategories(selectedBranch?.id).then(cats => {
      // Remove offer categories and grouped products categories by name
      const filtered = cats.filter(cat => !/^(العروض:|offer|deal|مجموعة:)/i.test(cat.CategoryName));
      // Sort by CategoriesOrder (nulls/undefined last)
      filtered.sort((a, b) => {
        const orderA = a.CategoriesOrder ?? 9999;
        const orderB = b.CategoriesOrder ?? 9999;
        return orderA - orderB;
      });
      setCategories(filtered);
    });
  }, [selectedBranch]);

  // Track which category section is in view
  useEffect(() => {
    const handleScroll = () => {
      // Use the center of the viewport for more accurate detection
      const viewportCenter = window.innerHeight / 2;
      let found = null;
      for (const cat of categories) {
        const el = document.getElementById(`category-section-${cat.Id}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= viewportCenter && rect.bottom > viewportCenter) {
            found = cat.Id;
            break;
          }
        }
      }
      if (found && activeCategory !== found) {
        setActiveCategory(found);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [categories, activeCategory]);

  // Auto-scroll the category bar horizontally to keep active category centered
  useEffect(() => {
    if (!activeCategory) return;
    const scrollEl = scrollRef.current;
    const catEl = document.getElementById(`cat-btn-${activeCategory}`);
    if (scrollEl && catEl) {
      const scrollAmount = catEl.offsetLeft - scrollEl.offsetWidth / 2 + catEl.offsetWidth / 2;
      scrollEl.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    }
  }, [activeCategory]);

  if (!branchStatus.isOpen) return null;

  return (
    <Box sx={{
      width: '100vw',
      maxWidth: '100vw',
      bgcolor: theme => theme.palette.common.white,
      backdropFilter: 'blur(12px)',
      py: { xs: 1, sm: 1.5 }, // ✅ Responsive padding
      mb: 0,
      boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
      height: { xs: 60, sm: 70, md: 80 }, // ✅ Responsive height
      px: 0,
      overflow: 'visible',
      borderRadius: 0,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'flex-start',
      borderBottom: 'none',
      position: 'sticky',
      // ✅ FIXED: Responsive top offset to stick below headers
      // On mobile, increase top offset to ensure bar is below all headers
      top: { xs: 90, sm: 80, md: 100 }, // xs: BlackHeader (26px) + Header (80px) or as needed
      zIndex: 1100, // ✅ Below headers (1200+)
    }}>
      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-end',
          justifyContent: 'flex-start',
          minWidth: 0,
          height: '100%',
          overflowX: 'auto',
          overflowY: 'hidden', // ✅ Prevent vertical scroll
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '::-webkit-scrollbar': { display: 'none' },
          gap: { xs: 2, sm: 3, md: 4 }, // ✅ Responsive gap
          width: '100%',
          px: { xs: 1, sm: 2 }, // ✅ Responsive padding
          // ✅ Enable smooth touch scrolling on mobile
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {categories.map(cat => (
          <Box
            component="span"
            key={cat.Id}
            id={`cat-btn-${cat.Id}`}
            onClick={() => {
              const element = document.getElementById(`category-section-${cat.Id}`);
              if (element) {
                // ✅ Responsive offset for sticky headers
                const offset = isMobile ? 146 : isTablet ? 176 : 206; // BlackHeader + Header + Categories
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - offset;
                window.scrollTo({
                  top: offsetPosition,
                  behavior: "smooth"
                });
              }
              // Center the clicked category in the bar
              const scrollEl = scrollRef.current;
              const catEl = document.getElementById(`cat-btn-${cat.Id}`);
              if (scrollEl && catEl) {
                const scrollAmount = catEl.offsetLeft - scrollEl.offsetWidth / 2 + catEl.offsetWidth / 2;
                scrollEl.scrollTo({ left: scrollAmount, behavior: 'smooth' });
              }
            }}
            sx={{
              display: 'inline-block',
              mx: { xs: 0.5, sm: 1 }, // ✅ Responsive margin
              color: '#111',
              fontWeight: activeCategory === cat.Id ? 'bold' : '500',
              px: { xs: 1.5, sm: 2, md: 2.5 }, // ✅ Responsive padding
              py: { xs: 0.3, sm: 0.5 }, // ✅ Responsive padding
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.2rem' }, // ✅ Responsive font size
              cursor: 'pointer',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              border: 'none',
              borderRadius: 0,
              position: 'relative',
              // ✅ Better mobile touch target
              minHeight: { xs: 44, sm: 48 }, // Apple's recommended touch target
              display: 'inline-flex',
              alignItems: 'center',
              '&:hover': {
                color: activeCategory !== cat.Id ? '#000' : undefined,
                bgcolor: activeCategory !== cat.Id ? 'action.hover' : undefined
              },
              // ✅ Active indicator
              '&::after': activeCategory === cat.Id ? {
                content: '""',
                display: 'block',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: { xs: '2px', sm: '3px' }, // ✅ Responsive indicator height
                background: '#111',
                borderRadius: 2,
              } : {},
            }}
          >
            {cat.CategoryName}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default Categories;
